import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { message } from 'antd';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { osOptions } from 'utils/constant';
import { theme } from 'utils/style/theme';
import InputFlexBox from 'components/container/InputFlexBox';
import InputWrappingBox from 'components/container/InputWrappingBox';
import SubmitFlexBox from 'components/container/SubmitFlexBox';
import DefaultInput from 'components/input/DefaultInput';
import RangeDatePicker from 'components/input/RangeDatePicker';
import DefaultLayout from 'components/layout/DefaultLayout';
import DefaultSelect from 'components/select/DefaultSelect';
import NewNoticeLocalizingForm from './NewNoticeLocalizingForm';
import UtcTimer from 'components/etc/UtcTimer';
import DefaultButton from 'components/button/DefaultButton';
import { useTranslation } from 'react-i18next';
import { noticeApis } from '../../../apis/operation/noticeApis';
import { INotice } from '../../../utils/types/operationTypes';

/**
 * * 판매 관리 - 인앱 상점 관리 - 등록 & 수정 페이지
 */

const NewNoticeInputPage = () => {
  const { t } = useTranslation();
  const { projectId, noticeId } = useParams();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<INotice>();
  const [isLink, setIsLink] = useState(false);
  const dateFormat = 'YYYY-MM-DD HH:mm:00';
  const today = dayjs().format(dateFormat);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setNotice({ ...notice!, [name]: value });
  };

  const handleSelectChange = (val, op) => {
    setNotice({ ...notice!, [op.name]: val });
  };

  /// 날짜 조절
  const handleDateChange = (dates, dateStrings) => {
    if (!dates) {
      setNotice({
        ...notice!,
        from_date: `${today}`,
        to_date: `${today}`,
      });
      return;
    }

    setNotice({
      ...notice!,
      from_date: `${dateStrings[0]}`,
      to_date: `${dateStrings[1]}`,
    });
  };

  const handleSubmit = async () => {
    if (notice === undefined || notice === null) {
      return;
    }

    if (notice.notice_name === '') {
      message.error('공지 이름을 입력해주세요');
      return false;
    }

    for (const detail of notice.details) {
      if (isLink) {
        if (detail.url_link === '') {
          message.error('링크를 입력해주세요');
          return false;
        }

        if (
          !detail.url_link.startsWith('http://') &&
          !detail.url_link.startsWith('https://')
        ) {
          message.error('링크 형식이 올바르지 않습니다.');
          return false;
        }

        if (detail.banner_url === '' || detail.banner_url === null) {
          message.error('배너 이미지를 입력해주세요');
          return false;
        }
      } else {
        if (detail.title === '') {
          message.error('제목을 입력해주세요');
          return false;
        }

        if (detail.contents === '') {
          message.error('내용을 입력해주세요');
          return false;
        }
      }
    }

    for (const detail of notice.details) {
      if (isLink) {
        detail.contents = '';
        detail.title = '';
      } else {
        detail.url_link = '';
      }
    }

    const res = await noticeApis.updateNotice(notice);

    if (res) {
      message.success('공지사항이 수정되었습니다.');
    } else {
      message.error('공지사항 수정에 실패했습니다.');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const fetchData = async () => {
    const res: INotice = await noticeApis.getNotice(
      Number(projectId),
      Number(noticeId),
    );

    setNotice(res);
    if (res.details.length > 0 && res.details[0].url_link) {
      setIsLink(true);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <DefaultLayout>
      <SubmitFlexBox>
        <>
          <DefaultButton onClick={handleGoBack}>
            {t('ifYouID.header.backButton')}
          </DefaultButton>
          <DefaultButton onClick={handleSubmit}>
            {t('ifYouID.header.submitButton')}
          </DefaultButton>
        </>
      </SubmitFlexBox>

      <InputFlexBox style={{ marginTop: '2.5rem' }}>
        <>
          {notice && (
            <>
              <InputWrappingBox left>
                <NewNoticeLocalizingForm
                  notice={notice}
                  setNotice={setNotice as Dispatch<SetStateAction<INotice>>}
                  isLink={isLink}
                  setIsLink={setIsLink}
                />
              </InputWrappingBox>

              <InputWrappingBox>
                <DefaultInput
                  title={t('ifYouID.table.productID')}
                  name='notice_id'
                  value={notice.notice_id}
                  onChange={handleInputChange}
                  disabled={true}
                />
                <DefaultInput
                  title={'공지 이름'}
                  name='notice_name'
                  value={notice.notice_name}
                  onChange={handleInputChange}
                />
                <DateSection>
                  <div>
                    <RangeDatePicker
                      title={'공지 기간'}
                      format={dateFormat}
                      showTime={true}
                      value={[
                        dayjs(notice.from_date, dateFormat),
                        dayjs(notice.to_date, dateFormat),
                      ]}
                      ranges={{
                        오늘: [dayjs(), dayjs()],
                        이번주: [
                          dayjs().startOf(today as any),
                          dayjs().endOf('week'),
                        ],
                        이번달: [
                          dayjs().startOf(today as any),
                          dayjs().endOf('month'),
                        ],
                      }}
                      onChange={handleDateChange}
                      style={{ paddingBottom: '0.5rem', paddingTop: '0.5rem' }}
                    />
                    <UtcTimer
                      onGrayBox={true}
                      startDate={notice.from_date ? notice.from_date : today}
                      endDate={notice.to_date ? notice.to_date : today}
                      style={{
                        marginTop: '0.2rem',
                        marginBottom: theme.margins.bottomInModal,
                      }}
                    />
                  </div>
                </DateSection>
                <DefaultSelect
                  title={'표시 플랫폼'}
                  name='os'
                  value={notice.os}
                  optionList={osOptions}
                  onChange={handleSelectChange}
                  style={{ marginBottom: '2rem' }}
                />
              </InputWrappingBox>
            </>
          )}
        </>
      </InputFlexBox>
    </DefaultLayout>
  );
};

export default NewNoticeInputPage;

const DateSection = styled.div`
  display: flex;
  align-items: center;

  .option_wrap {
    margin-top: -1rem;
    display: flex;
  }

  .checkbox_wrap {
    display: flex;
    align-items: center;
    margin-left: 1rem;

    > p {
      margin-left: 0.5rem;
    }
  }
`;
